// The address a published post links to: /listen/155 plays show 155 in the archive.
//
// A mode of the archive page rather than a page of its own, for the same reason /review is:
// the header, the list, the player and the canvas are what a listener arriving from a post
// wants to land in, and a second layout would be a second thing to keep working.
//
// The URL is left standing. It is a real bookmarkable address, and it names the row the
// player is on, so a reload replays it rather than resuming somewhere else.

import { useEffect, useRef } from "react";
import { api } from "../api/client";
import type { Segment } from "../api/types";
import { usePlayer } from "../state/player";

// The id is the whole of it; anything after it is the slug a post prints for its reader, and
// is deliberately not resolved -- a retitled show keeps every link already published working.
const PATH = /^\/listen\/(\d+)(?:\/.*)?$/;

/** The show id the path names, or null when it names something else. */
export function listenId(path: string): number | null {
  const id = PATH.exec(path)?.[1];
  return id ? Number(id) : null;
}

/**
 * Hands the show the path names to the player, once.
 *
 * Once per id, tracked in a ref rather than off the store: the listener may well press pause,
 * or click another row, and re-running the effect -- which any re-render of `App` does -- must
 * not drag them back. Autoplay refusal is not handled here; `useAudioSession` already treats it
 * as a loaded, paused show, which is the most a tab with no gesture in it is allowed to do.
 */
export function useListenLink(path: string): void {
  const handled = useRef<number | null>(null);

  useEffect(() => {
    const id = listenId(path);
    if (id === null || handled.current === id) return;
    handled.current = id;
    void api.get<Segment>(`/api/segments/${id}`).then(
      (segment) => {
        const state = usePlayer.getState();
        // The same gesture as clicking the row in the list: selected, played, and followed.
        // Unplayable rows are handed over too -- the player's own loader is what refuses one
        // and says why, and routing it there is what makes the pane name this show.
        state.select(segment.id);
        state.play({ kind: "segment", segment });
        state.setFollow(true);
      },
      () => {
        // A deleted show, or a link with a number nothing answers to. The archive page is
        // already rendered behind this, which is a better answer than an error over it.
      },
    );
  }, [path]);
}
